import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const HomePage: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const handleMobileNavClick = () => {
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < lastScrollY) {
        // Scrolling up
        setIsHeaderVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down
        setIsHeaderVisible(false);
        setIsMobileMenuOpen(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 bg-white shadow-sm z-50 transition-transform duration-300 ${
        isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <h1 className="text-2xl font-bold select-none" style={{ fontFamily: '"Pacifico", serif' }}>
                Novatronis
              </h1>
            </Link>
            <div className="hidden md:flex space-x-8">
              <Link to="/" className="hover:text-blue-400 transition-colors cursor-pointer">Home</Link>
              <Link to="/services" className="hover:text-blue-400 transition-colors cursor-pointer">Services</Link>
              <Link to="/solutions" className="hover:text-blue-400 transition-colors cursor-pointer">Solutions</Link>
              <Link to="/about" className="hover:text-blue-400 transition-colors cursor-pointer">About</Link>
              <Link to="/contact" className="hover:text-blue-400 transition-colors cursor-pointer">Contact</Link>
            </div>
            <div className="md:hidden">
              <button 
                className="text-gray-600 cursor-pointer"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
            </div>
          </div>
          
          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden bg-white border-t border-gray-200 mt-4 py-4">
              <div className="flex flex-col space-y-4">
                <Link 
                  to="/" 
                  className="text-blue-400 px-6 py-2 cursor-pointer"
                  onClick={handleMobileNavClick}
                >
                  Home
                </Link>
                <Link 
                  to="/services" 
                  className="hover:text-blue-400 transition-colors px-6 py-2 cursor-pointer"
                  onClick={handleMobileNavClick}
                >
                  Services
                </Link>
                <Link 
                  to="/solutions" 
                  className="hover:text-blue-400 transition-colors px-6 py-2 cursor-pointer"
                  onClick={handleMobileNavClick}
                >
                  Solutions
                </Link>
                <Link 
                  to="/about" 
                  className="hover:text-blue-400 transition-colors px-6 py-2 cursor-pointer"
                  onClick={handleMobileNavClick}
                >
                  About
                </Link>
                <Link 
                  to="/contact" 
                  className="hover:text-blue-400 transition-colors px-6 py-2 cursor-pointer"
                  onClick={handleMobileNavClick}
                >
                  Contact
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-blue-900 to-black text-white pt-20">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/cf1aaed3c79e52b03be9f8799ca8e2d4.jpg')`
          }}
        ></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <div className="mb-8">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="block">Smart Warehouse</span>
              <span className="block text-blue-400">Automation</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto text-gray-300">
              Transform your warehouse operations with cutting-edge robotics, AI-powered systems, and intelligent automation solutions
            </p>
          </div>
        </div>
      </section>

      {/* Packing Solutions Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Advanced Packing Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Revolutionize your packaging operations with our cutting-edge automated packing systems designed for maximum efficiency and precision
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Advanced%20automated%20packing%20system%20with%20robotic%20arms%20packaging%20products%20in%20modern%20warehouse%20facility%20with%20conveyor%20belts%20and%20smart%20technology%20integration%20for%20efficient%20packaging%20operations&width=800&height=600&seq=packing-main-001&orientation=landscape"
                alt="Advanced Packing Automation System"
                className="rounded-xl shadow-2xl object-cover object-top"
              />
            </div>
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                Intelligent Automated Packing
              </h3>
              <p className="text-lg text-gray-600 mb-6">
                Our state-of-the-art packing solutions combine robotics, AI, and machine learning to deliver unparalleled packaging efficiency. From product identification to final packaging, every step is optimized for speed and accuracy.
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-check-line text-blue-600"></i>
                  </div>
                  <span className="text-gray-700">99.8% packaging accuracy rate</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-check-line text-blue-600"></i>
                  </div>
                  <span className="text-gray-700">Up to 300% faster than manual packing</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-check-line text-blue-600"></i>
                  </div>
                  <span className="text-gray-700">Reduces packaging material waste by 40%</span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-8 rounded-xl">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-robot-line text-2xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4">Robotic Packing Arms</h4>
              <p className="text-gray-600">
                Precision robotic systems that handle delicate items with care while maintaining high-speed operations for maximum throughput.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-qr-code-line text-2xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4">Smart Labeling System</h4>
              <p className="text-gray-600">
                Automated labeling with real-time tracking integration, ensuring accurate shipping information and seamless logistics coordination.
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mb-6">
                <i className="ri-leaf-line text-2xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-4">Sustainable Packaging</h4>
              <p className="text-gray-600">
                Eco-friendly packaging solutions that optimize material usage while maintaining product protection and reducing environmental impact.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Solutions */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Industry-Specific Automation Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tailored warehouse automation for every sector, optimizing efficiency and reducing operational costs
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-shopping-cart-line',
                title: 'E-Commerce',
                description: 'High-speed order fulfillment, automated picking, and real-time inventory management for online retailers'
              },
              {
                icon: 'ri-store-line',
                title: 'Retail',
                description: 'Streamlined distribution, automated replenishment, and multi-channel inventory optimization'
              },
              {
                icon: 'ri-settings-line',
                title: 'Manufacturing',
                description: 'Automated material handling, production line integration, and just-in-time inventory systems'
              },
              {
                icon: 'ri-medicine-bottle-line',
                title: 'Pharmaceuticals',
                description: 'Temperature-controlled storage, compliance tracking, and secure automated dispensing systems'
              },
              {
                icon: 'ri-restaurant-line',
                title: 'Food & Beverage',
                description: 'Cold chain automation, FIFO inventory rotation, and food safety compliance systems'
              },
              {
                icon: 'ri-truck-line',
                title: 'Logistics',
                description: 'Cross-docking automation, sortation systems, and multi-carrier shipping optimization'
              }
            ].map((industry, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <i className={`${industry.icon} text-2xl text-blue-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{industry.title}</h3>
                <p className="text-gray-600">{industry.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Advanced Technology Stack
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Cutting-edge automation technologies that power the future of warehouse operations
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Advanced%20warehouse%20automation%20technology%20stack%20with%20robotic%20arms%2C%20AGVs%2C%20conveyor%20systems%2C%20and%20AI%20control%20panels%20in%20a%20modern%20facility%20with%20blue%20accent%20lighting&width=800&height=600&seq=tech-stack-001&orientation=landscape"
                alt="Advanced Warehouse Technology"
                className="rounded-xl shadow-2xl"
              />
            </div>
            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-robot-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Robotic Automation</h3>
                  <p className="text-gray-300">Advanced robotic picking, packing, and sorting systems with AI-powered decision making</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-cpu-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">AI-Powered Software</h3>
                  <p className="text-gray-300">Machine learning algorithms for predictive analytics, optimization, and real-time decision making</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-car-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Autonomous Vehicles</h3>
                  <p className="text-gray-300">AGVs and AMRs for automated material transport and inventory management</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-cloud-line text-xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Cloud Integration</h3>
                  <p className="text-gray-300">Seamless integration with existing systems and real-time data analytics</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Complete Automation Solutions */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Complete Automation Solutions
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              From goods-to-person systems to advanced sortation, we provide comprehensive automation technologies
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: 'Goods-to-Person',
                items: ['AMR Rack-to-Person', '4-way Pallet Shuttle', 'Multi-shuttle Systems', 'Automated Storage']
              },
              {
                title: 'Sortation Systems',
                items: ['High-speed Sorters', 'Cross-belt Sorters', 'Tilt-tray Systems', 'Parcel Sortation']
              },
              {
                title: 'AGV Solutions',
                items: ['Pallet Movers', 'Reach Trucks', 'Forklift Automation', 'Custom AGVs']
              },
              {
                title: 'Smart Systems',
                items: ['Pick-to-Light', 'Voice Picking', 'Robotic Palletizers', 'WMS Integration']
              }
            ].map((category, index) => (
              <div key={index} className="bg-gray-800 p-6 rounded-xl">
                <h3 className="text-xl font-bold mb-4 text-blue-400">{category.title}</h3>
                <ul className="space-y-2">
                  {category.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="text-gray-300 flex items-center">
                      <i className="ri-check-line text-blue-400 mr-2"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Removed mobile bottom navigation bar */}

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <h3 className="text-2xl font-bold mb-4 select-none" style={{ fontFamily: '"Pacifico", serif' }}>
                Novatronis
              </h3>
              <p className="text-gray-400 max-w-2xl mx-auto">
                Transforming warehouse operations with cutting-edge robotics, AI-powered systems, and intelligent automation solutions. We empower businesses to achieve unprecedented levels of efficiency, accuracy, and cost savings through innovative technology.
              </p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400">
              © 2024 Novatronis Solutions. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
